import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Truck, Plus, Trash2, Edit3, UploadCloud, X, CheckCircle2,
  AlertTriangle, Eye, Users as UsersIcon, FileText, Calendar,
} from "lucide-react";

const VEHICLE_MAKES = [
  "Freightliner", "Peterbilt", "Kenworth", "Volvo", "Mack",
  "International", "Western Star", "Ford", "Other",
];

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function expiryTag(iso) {
  if (!iso) return <span className="text-slate-400 font-mono text-xs">—</span>;
  const d = daysUntil(iso);
  const short = iso.slice(0, 10);
  if (d < 0) return <span className="hs-tag hs-tag-danger">EXPIRED</span>;
  if (d <= 30) return <span className="hs-tag hs-tag-danger">{d}d · {short}</span>;
  if (d <= 60) return <span className="hs-tag hs-tag-warn">{d}d · {short}</span>;
  return <span className="hs-tag hs-tag-success">{short}</span>;
}

function completion(v) {
  const items = [v.irp_added, v.insurance_added, v.annual_dot_done, v.brake_90d_done];
  const done = items.filter(Boolean).length;
  return { done, total: items.length, pct: Math.round((done / items.length) * 100) };
}

export default function Vehicles({ adminClientId }) {
  const { user } = useAuth();
  const isAdminView = !!adminClientId;
  const qs = adminClientId ? `?client_id=${adminClientId}` : "";
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d] = await Promise.all([
        api.get(`/vehicles${qs}`),
        api.get(`/drivers${qs}`),
      ]);
      setVehicles(v.data);
      setDrivers(d.data);
    } finally { setLoading(false); }
  }, [qs]);
  useEffect(() => { load(); }, [load]);

  const remove = async (v) => {
    const ok = window.confirm(`Delete vehicle ${v.unit_number || v.vin}?`);
    if (ok) {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Vehicle removed");
      load();
    }
  };

  const dotExpiring = vehicles.filter(v => {
    const d = daysUntil(v.annual_dot_expires);
    return d !== null && d <= 60;
  }).length;

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {!adminClientId && (
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="hs-overline text-blue-700">FLEET · VEHICLES</div>
            <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Trucks & Trailers</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Track each truck&apos;s VIN, registrations, inspections, and assigned drivers — one tile at a glance.
            </p>
          </div>
          <button
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="hs-btn-primary inline-flex items-center gap-2"
            data-testid="add-vehicle-button"
          >
            <Plus size={14}/> ADD TRUCK
          </button>
        </div>
      )}
      {adminClientId && (
        <div className="flex items-center justify-between">
          <div className="hs-overline">TRUCKS ({vehicles.length})</div>
          <button onClick={() => { setEditing(null); setShowModal(true); }} className="hs-btn-primary inline-flex items-center gap-2" data-testid="add-vehicle-button">
            <Plus size={14}/> ADD TRUCK
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat icon={Truck} label="Active Trucks" value={vehicles.filter(v => v.status === "active").length} />
        <Stat icon={AlertTriangle} label="DOT Inspection 60d" value={dotExpiring} accent={dotExpiring ? "#D97706" : "#059669"} />
        <Stat icon={UsersIcon} label="Trucks w/ Driver" value={vehicles.filter(v => (v.assigned_driver_ids || []).length > 0).length} />
        <Stat icon={AlertTriangle} label="OOS / Sold" value={vehicles.filter(v => v.status !== "active").length} accent="#475569" />
      </div>

      {/* Tile grid */}
      {loading ? (
        <div className="hs-overline">LOADING…</div>
      ) : vehicles.length === 0 ? (
        <div className="hs-card p-12 text-center text-sm text-slate-500" data-testid="vehicles-empty">
          No trucks on file. Click &quot;Add Truck&quot; to add your first vehicle.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v) => {
            const c = completion(v);
            return (
              <div key={v.id} className="hs-card p-4 space-y-3 hs-card-hover" data-testid={`vehicle-card-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Truck size={14} className="text-blue-700 flex-shrink-0" />
                      <span className="font-bold text-sm">{v.unit_number || v.vin}</span>
                      <span className={`hs-tag ${v.status === "active" ? "hs-tag-success" : "hs-tag-warn"} text-[10px]`}>
                        {(v.status || "active").toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 font-mono mt-1 truncate">{v.vin}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {v.year} {v.make} {v.model}
                    </div>
                    {v.license_plate && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        Plate: {v.license_plate}{v.license_state && ` · ${v.license_state}`}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setDetailId(v.id)} className="hs-btn-ghost p-1.5" title="Details" data-testid={`vehicle-view-${v.id}`}>
                      <Eye size={13}/>
                    </button>
                    <button onClick={() => { setEditing(v); setShowModal(true); }} className="hs-btn-ghost p-1.5" title="Edit" data-testid={`vehicle-edit-${v.id}`}>
                      <Edit3 size={13}/>
                    </button>
                    <button onClick={() => remove(v)} className="hs-btn-ghost p-1.5 text-red-600" title="Delete" data-testid={`vehicle-delete-${v.id}`}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>

                {/* Completion bar */}
                <div>
                  <div className="flex items-center justify-between text-[10px] hs-overline mb-1">
                    <span>Compliance</span>
                    <span>{c.done}/{c.total}</span>
                  </div>
                  <div className="h-1.5 bg-slate-200 overflow-hidden">
                    <div
                      className={`h-full ${c.pct === 100 ? "bg-emerald-500" : c.pct >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                      style={{ width: `${c.pct}%` }}
                    />
                  </div>
                </div>

                {/* Checklist mini */}
                <div className="grid grid-cols-2 gap-1.5">
                  <Mini ok={v.irp_added} label="IRP" />
                  <Mini ok={v.insurance_added} label="Insurance" />
                  <Mini ok={v.annual_dot_done} label="DOT Insp" />
                  <Mini ok={v.brake_90d_done} label="Brake 90d" />
                </div>

                {/* DOT expiry */}
                <div className="flex items-center justify-between text-xs">
                  <span className="hs-overline">DOT Inspection Expires</span>
                  {expiryTag(v.annual_dot_expires)}
                </div>

                {/* Assigned drivers */}
                <div className="border-t border-slate-100 pt-2">
                  <div className="hs-overline text-[10px] mb-1">Assigned Drivers ({(v.assigned_drivers || []).length}/2)</div>
                  {(v.assigned_drivers || []).length === 0 ? (
                    <div className="text-xs text-slate-400">No driver assigned</div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {v.assigned_drivers.map((d, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">
                          {d.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <VehicleModal
          initial={editing}
          drivers={drivers}
          qs={qs}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={async () => { setShowModal(false); setEditing(null); await load(); toast.success("Vehicle saved"); }}
        />
      )}
      {detailId && (
        <VehicleDetail vehicleId={detailId} drivers={drivers} onClose={() => { setDetailId(null); load(); }} />
      )}
    </div>
  );
}

function Mini({ ok, label }) {
  return (
    <div className={`flex items-center gap-1 text-[11px] px-1.5 py-1 border ${
      ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-400"
    }`}>
      <CheckCircle2 size={10} className={ok ? "text-emerald-600" : "text-slate-300"} />
      {label}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "#1D4ED8" }) {
  return (
    <div className="hs-card p-4">
      <div className="flex items-center justify-between">
        <span className="hs-overline">{label}</span>
        <Icon size={14} style={{ color: accent }} />
      </div>
      <div className="font-mono font-black text-2xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function VehicleModal({ initial, drivers, qs, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    vin: "", year: "", make: "Freightliner", model: "", license_plate: "", license_state: "",
    gvw: "", unit_number: "", status: "active", notes: "",
    irp_added: false, insurance_added: false,
    annual_dot_done: false, annual_dot_date: "", annual_dot_expires: "",
    brake_90d_done: false, brake_90d_date: "",
    assigned_driver_ids: [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: !form[k] });

  const setDriver = (slot) => (e) => {
    const id = e.target.value;
    const ids = [...(form.assigned_driver_ids || [])];
    while (ids.length < 2) ids.push(null);
    ids[slot] = id || null;
    setForm({ ...form, assigned_driver_ids: ids.filter(Boolean) });
  };

  const save = async () => {
    if (!form.vin?.trim()) { toast.error("VIN is required"); return; }
    if (form.vin.length > 17) { toast.error("VIN must be 17 characters or less"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      payload.vin = payload.vin.toUpperCase().trim();
      payload.year = payload.year ? parseInt(payload.year, 10) : null;
      payload.gvw = payload.gvw ? parseInt(payload.gvw, 10) : null;
      for (const k of ["annual_dot_date", "annual_dot_expires", "brake_90d_date"]) {
        if (payload[k]) payload[k] = payload[k].slice(0, 10);
      }
      if (initial?.id) await api.put(`/vehicles/${initial.id}`, payload);
      else await api.post(`/vehicles${qs}`, payload);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const driver1 = (form.assigned_driver_ids || [])[0] || "";
  const driver2 = (form.assigned_driver_ids || [])[1] || "";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vehicle-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="hs-overline">{initial ? "EDIT" : "ADD"} VEHICLE</div>
            <h3 className="font-display font-bold text-2xl mt-1">{initial?.unit_number || initial?.vin || "New Truck"}</h3>
          </div>
          <button onClick={onClose} className="hs-btn-ghost p-2"><X size={16}/></button>
        </div>

        <div className="hs-overline mt-6 mb-2">IDENTITY</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="VIN (17 chars)" required value={form.vin} onChange={set("vin")} testid="vehicle-vin" maxLength={17}/>
          <Field label="Unit Number" value={form.unit_number || ""} onChange={set("unit_number")} testid="vehicle-unit" placeholder="T-101"/>
          <Field label="Year" type="number" value={form.year || ""} onChange={set("year")} testid="vehicle-year"/>
          <div>
            <label className="hs-overline">Make</label>
            <select className="hs-input mt-2" value={form.make || ""} onChange={set("make")} data-testid="vehicle-make">
              <option value="">—</option>
              {VEHICLE_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <Field label="Model" value={form.model || ""} onChange={set("model")} testid="vehicle-model"/>
          <Field label="GVW (lbs)" type="number" value={form.gvw || ""} onChange={set("gvw")} testid="vehicle-gvw"/>
          <Field label="License Plate" value={form.license_plate || ""} onChange={set("license_plate")} testid="vehicle-plate"/>
          <Field label="License State" value={form.license_state || ""} onChange={set("license_state")} testid="vehicle-license-state" maxLength={2}/>
          <div>
            <label className="hs-overline">Status</label>
            <select className="hs-input mt-2" value={form.status} onChange={set("status")} data-testid="vehicle-status">
              <option value="active">Active</option>
              <option value="out_of_service">Out of Service</option>
              <option value="sold">Sold</option>
            </select>
          </div>
        </div>

        <div className="hs-overline mt-6 mb-2">COMPLIANCE CHECKLIST</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <CheckBox checked={form.irp_added} onToggle={toggle("irp_added")} label="Added to IRP" testid="vehicle-chk-irp"/>
          <CheckBox checked={form.insurance_added} onToggle={toggle("insurance_added")} label="Added to Insurance" testid="vehicle-chk-insurance"/>
          <CheckWithDate
            checked={form.annual_dot_done}
            onToggle={toggle("annual_dot_done")}
            dateLabel="Inspection date"
            dateValue={(form.annual_dot_date || "").slice(0, 10)}
            onDateChange={set("annual_dot_date")}
            label="Annual DOT Inspection"
            testid="vehicle-chk-dot"
            extraDateLabel="Expires"
            extraDateValue={(form.annual_dot_expires || "").slice(0, 10)}
            onExtraDateChange={set("annual_dot_expires")}
          />
          <CheckWithDate
            checked={form.brake_90d_done}
            onToggle={toggle("brake_90d_done")}
            dateLabel="Last brake inspection"
            dateValue={(form.brake_90d_date || "").slice(0, 10)}
            onDateChange={set("brake_90d_date")}
            label="90-Day Brake Inspection"
            testid="vehicle-chk-brake"
          />
        </div>

        <div className="hs-overline mt-6 mb-2">ASSIGNED DRIVERS (Max 2 — for team driving)</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="hs-overline">Primary Driver</label>
            <select className="hs-input mt-2" value={driver1} onChange={setDriver(0)} data-testid="vehicle-driver-1">
              <option value="">—</option>
              {drivers.filter(d => d.status === "active").map(d => (
                <option key={d.id} value={d.id} disabled={d.id === driver2}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="hs-overline">Co-Driver (Optional)</label>
            <select className="hs-input mt-2" value={driver2} onChange={setDriver(1)} data-testid="vehicle-driver-2">
              <option value="">—</option>
              {drivers.filter(d => d.status === "active").map(d => (
                <option key={d.id} value={d.id} disabled={d.id === driver1}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="hs-overline">Notes</label>
          <textarea className="hs-input mt-2 min-h-[60px]" value={form.notes || ""} onChange={set("notes")} data-testid="vehicle-notes"/>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="hs-btn-secondary">CANCEL</button>
          <button onClick={save} disabled={busy} className="hs-btn-primary" data-testid="vehicle-save">
            {busy ? "SAVING…" : "SAVE VEHICLE"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VehicleDetail({ vehicleId, drivers, onClose }) {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [inspectionDate, setInspectionDate] = useState("");
  const [inspectionLevel, setInspectionLevel] = useState("");
  const [notes, setNotes] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/vehicles/${vehicleId}`);
    setData(data);
  }, [vehicleId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) {
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (inspectionDate) fd.append("inspection_date", inspectionDate);
    if (inspectionLevel) fd.append("inspection_level", inspectionLevel);
    if (notes) fd.append("notes", notes);
    setUploading(true);
    try {
      await api.post(`/vehicles/${vehicleId}/inspection-report`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Inspection report uploaded");
      setInspectionDate(""); setInspectionLevel(""); setNotes("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!data) return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={onClose}>
      <div className="hs-card p-6 text-sm hs-overline">LOADING…</div>
    </div>
  );

  const v = data.vehicle;
  const reports = data.inspection_reports || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vehicle-detail">
        <div className="flex items-start justify-between">
          <div>
            <div className="hs-overline">VEHICLE PROFILE</div>
            <h3 className="font-display font-bold text-2xl mt-1">{v.unit_number || v.vin}</h3>
            <div className="text-sm text-slate-500 mt-0.5">
              {v.year} {v.make} {v.model} · VIN <span className="font-mono">{v.vin}</span>
            </div>
          </div>
          <button onClick={onClose} className="hs-btn-ghost p-2"><X size={16}/></button>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
          <Info k="License" v={`${v.license_plate || "—"} ${v.license_state ? "· " + v.license_state : ""}`}/>
          <Info k="GVW" v={v.gvw ? `${v.gvw.toLocaleString()} lbs` : "—"}/>
          <Info k="Status" v={(v.status || "active").toUpperCase()}/>
          <Info k="DOT Inspection Expires" v={v.annual_dot_expires || "—"}/>
        </div>

        <div className="mt-6">
          <div className="hs-overline mb-2">Assigned Drivers</div>
          {(data.assigned_drivers || []).length === 0 ? (
            <div className="text-xs text-slate-400">No driver assigned yet — edit this truck to assign.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.assigned_drivers.map(d => (
                <span key={d.id} className="text-sm px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200">
                  {d.name}{d.cdl_number && <span className="text-blue-400"> · CDL {d.cdl_number}</span>}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 border-t border-slate-200 pt-4">
          <div className="hs-overline mb-2">UPLOAD INSPECTION REPORT</div>
          <div className="grid sm:grid-cols-3 gap-2 mb-3">
            <input type="date" className="hs-input text-sm" value={inspectionDate} onChange={(e) => setInspectionDate(e.target.value)} data-testid="vehicle-insp-date"/>
            <select className="hs-input text-sm" value={inspectionLevel} onChange={(e) => setInspectionLevel(e.target.value)} data-testid="vehicle-insp-level">
              <option value="">Level…</option>
              <option value="Level 1">Level 1 (Full)</option>
              <option value="Level 2">Level 2 (Walk-around)</option>
              <option value="Level 3">Level 3 (Driver only)</option>
              <option value="Level 4">Level 4 (Special)</option>
              <option value="Level 5">Level 5 (Vehicle only)</option>
              <option value="Level 6">Level 6 (Radioactive)</option>
            </select>
            <input type="text" className="hs-input text-sm" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="vehicle-insp-notes"/>
          </div>
          <input
            type="file"
            ref={fileRef}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="vehicle-insp-file"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="hs-btn-primary inline-flex items-center gap-2"
            data-testid="vehicle-insp-upload-btn"
          >
            <UploadCloud size={14}/> {uploading ? "UPLOADING…" : "UPLOAD INSPECTION REPORT"}
          </button>
        </div>

        <div className="mt-6">
          <div className="hs-overline mb-2">INSPECTION HISTORY ({reports.length})</div>
          {reports.length === 0 ? (
            <div className="text-xs text-slate-400">No inspection reports uploaded yet.</div>
          ) : (
            <div className="space-y-2">
              {reports.map(r => (
                <div key={r.id} className="border border-slate-200 p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <FileText size={13} className="text-blue-700 flex-shrink-0"/>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${r.id}/download`}
                        target="_blank" rel="noreferrer"
                        className="text-sm font-medium hover:text-blue-700 truncate"
                        data-testid={`vehicle-report-${r.id}`}
                      >
                        {r.filename}
                      </a>
                      {r.inspection_level && <span className="hs-tag hs-tag-info text-[10px]">{r.inspection_level}</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Uploaded {(r.uploaded_at || "").slice(0, 10)}
                      {r.inspection_date && <> · Inspected {r.inspection_date.slice(0, 10)}</>}
                      {r.notes && <> · {r.notes}</>}
                    </div>
                  </div>
                  <Calendar size={13} className="text-slate-300"/>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, className = "", required, ...rest }) {
  return (
    <div className={className}>
      <label className="hs-overline">{label}{required && <span className="text-red-500"> *</span>}</label>
      <input className="hs-input mt-2" data-testid={testid} {...rest}/>
    </div>
  );
}

function Info({ k, v }) {
  return (
    <div className="border border-slate-200 p-2.5">
      <div className="hs-overline text-[10px]">{k}</div>
      <div className="text-sm font-mono mt-1">{v ?? "—"}</div>
    </div>
  );
}

function CheckBox({ checked, onToggle, label, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={`flex items-center gap-3 p-3 border text-left transition ${
        checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
    >
      <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
        checked ? "border-emerald-600 bg-emerald-600" : "border-slate-300"
      }`}>
        {checked && <CheckCircle2 size={12} className="text-white" />}
      </div>
      <span className={`text-sm ${checked ? "font-bold text-emerald-900" : "text-slate-700"}`}>{label}</span>
    </button>
  );
}

function CheckWithDate({ checked, onToggle, dateLabel, dateValue, onDateChange, label, testid, extraDateLabel, extraDateValue, onExtraDateChange }) {
  return (
    <div className={`p-3 border ${checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
      <button type="button" onClick={onToggle} data-testid={testid} className="flex items-center gap-3 text-left w-full">
        <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
          checked ? "border-emerald-600 bg-emerald-600" : "border-slate-300"
        }`}>
          {checked && <CheckCircle2 size={12} className="text-white" />}
        </div>
        <span className={`text-sm ${checked ? "font-bold text-emerald-900" : "text-slate-700"}`}>{label}</span>
      </button>
      {checked && (
        <div className="mt-2 space-y-1">
          <div>
            <label className="hs-overline text-[9px]">{dateLabel || "Date"}</label>
            <input type="date" className="hs-input text-xs" value={dateValue || ""} onChange={onDateChange} data-testid={`${testid}-date`}/>
          </div>
          {extraDateLabel && (
            <div>
              <label className="hs-overline text-[9px]">{extraDateLabel}</label>
              <input type="date" className="hs-input text-xs" value={extraDateValue || ""} onChange={onExtraDateChange} data-testid={`${testid}-expires`}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
